import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Send,
  Clock,
  Edit2,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { DD, POST_TYPES } from "./constants";
import { computeKwScore, getPostDate } from "./utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/hooks/useTracking";
import type { Post, Keyword } from "@shared/schema";

function KanbanCard({
  post,
  keywords,
  onEdit,
}: {
  post: Post;
  keywords: Keyword[];
  onEdit: (p: Post) => void;
}) {
  const pt = POST_TYPES.find(t => t.value === post.postType) || POST_TYPES[0];
  const kwScore = computeKwScore(post.content, keywords);
  const d = getPostDate(post);

  return (
    <div
      className="rounded-lg p-3 space-y-2 cursor-pointer hover:bg-white/[0.02] transition-colors"
      style={{ background: DD.surface, border: `1px solid ${DD.border}` }}
      onClick={() => onEdit(post)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", post.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      data-testid={`kanban-card-${post.id}`}
    >
      <div className="flex items-center gap-2">
        <Badge className="text-[9px] font-bold border-none px-1.5 py-0" style={{ color: pt.color, background: `${pt.color}15` }}>
          {pt.icon} {pt.label}
        </Badge>
        {d && <span className="text-[10px] text-white/20 font-mono">{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
      </div>

      <p className="text-xs text-white/50 line-clamp-2">{post.content?.slice(0, 100) || "Empty"}</p>

      {post.mediaUrl && (
        <div className="w-full h-16 rounded overflow-hidden" style={{ border: `1px solid ${DD.border}` }}>
          <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {kwScore > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: kwScore >= 60 ? "rgba(34,197,94,0.08)" : "rgba(249,115,22,0.10)", color: kwScore >= 60 ? DD.green : DD.accent }}>
              KW {kwScore}%
            </span>
          )}
          {!post.mediaUrl && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">No Image</span>
          )}
        </div>
        <div className="flex gap-1">
          <button className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-white hover:bg-white/5 transition-colors" onClick={e => { e.stopPropagation(); onEdit(post); }} data-testid={`button-kanban-edit-${post.id}`}>
            <Edit2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function KanbanBoard({
  posts,
  keywords,
  locationId,
  onClickPost,
  onPublishSuccess,
}: {
  posts: Post[];
  keywords: Keyword[];
  locationId: string;
  onClickPost: (p: Post) => void;
  onPublishSuccess?: (kw?: string) => void;
}) {
  const { toast } = useToast();
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const columns = useMemo(() => {
    const needsAttention = posts.filter(p =>
      p.status === "draft" || p.approvalStatus === "pending" || (!p.mediaUrl && p.status !== "published")
    );
    const readyToPublish = posts.filter(p =>
      p.status !== "published" && p.status !== "scheduled" &&
      p.approvalStatus !== "pending" && p.status !== "draft" &&
      p.mediaUrl
    );
    const scheduled = posts.filter(p => p.status === "scheduled");

    return [
      { id: "needs-attention", title: "Needs Attention", posts: needsAttention, color: DD.warning, icon: <AlertTriangle className="w-3.5 h-3.5" /> },
      { id: "ready", title: "Ready to Publish", posts: readyToPublish, color: DD.green, icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
      { id: "scheduled", title: "Scheduled", posts: scheduled, color: DD.indigo, icon: <Clock className="w-3.5 h-3.5" /> },
    ];
  }, [posts]);

  const publishMutation = useMutation({
    mutationFn: async (post: Post) => {
      let gbpPublished = false;
      try {
        const res = await apiRequest("POST", "/api/gbp/posts/publish", {
          locationId,
          postText: post.content,
          ctaType: post.ctaType,
          ctaUrl: post.ctaUrl,
          mediaUrl: post.mediaUrl,
          postType: post.postType,
          couponCode: post.couponCode,
          eventStartDate: post.eventStartDate,
          eventEndDate: post.eventEndDate,
          title: post.title,
        });
        const data = await res.json();
        gbpPublished = data?.published === true;
      } catch {
        gbpPublished = false;
      }
      await apiRequest("PATCH", `/api/posts/${post.id}`, {
        status: gbpPublished ? "published" : "scheduled",
        publishedAt: gbpPublished ? new Date().toISOString() : undefined,
        approvalStatus: "approved",
      });
      return { post, gbpPublished };
    },
    onSuccess: ({ post, gbpPublished }) => {
      trackEvent('gbp_post_published', { post_id: post.id });
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      if (gbpPublished) {
        const kw = post.targetKeyword || undefined;
        onPublishSuccess?.(kw);
      } else {
        toast({ title: "Post saved & scheduled", description: "Could not publish to Google — your GBP connection needs re-authorization." });
      }
    },
    onError: (err: Error) => toast({ title: "Publish failed", description: err.message, variant: "destructive" }),
  });

  const scheduleMutation = useMutation({
    mutationFn: (postId: string) => apiRequest("PATCH", `/api/posts/${postId}`, { status: "scheduled", scheduledAt: new Date().toISOString(), approvalStatus: "approved" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Post scheduled" });
    },
    onError: (err: Error) => toast({ title: "Failed to schedule", description: err.message, variant: "destructive" }),
  });

  const moveToDraftMutation = useMutation({
    mutationFn: (postId: string) => apiRequest("PATCH", `/api/posts/${postId}`, { status: "draft", approvalStatus: "draft" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Post moved to drafts" });
    },
    onError: (err: Error) => toast({ title: "Failed to move", description: err.message, variant: "destructive" }),
  });

  const moveToReadyMutation = useMutation({
    mutationFn: (postId: string) => apiRequest("PATCH", `/api/posts/${postId}`, { status: "approved", approvalStatus: "approved" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Post marked as ready" });
    },
    onError: (err: Error) => toast({ title: "Failed to update", description: err.message, variant: "destructive" }),
  });

  const bulkPublishMutation = useMutation({
    mutationFn: async () => {
      const readyPosts = columns.find(c => c.id === "ready")?.posts || [];
      let gbpCount = 0;
      let savedCount = 0;
      for (const post of readyPosts) {
        let gbpPublished = false;
        try {
          const res = await apiRequest("POST", "/api/gbp/posts/publish", {
            locationId,
            postText: post.content,
            ctaType: post.ctaType,
            ctaUrl: post.ctaUrl,
            mediaUrl: post.mediaUrl,
            postType: post.postType,
            couponCode: post.couponCode,
            eventStartDate: post.eventStartDate,
            eventEndDate: post.eventEndDate,
            title: post.title,
          });
          const data = await res.json();
          gbpPublished = data?.published === true;
        } catch {
          gbpPublished = false;
        }
        await apiRequest("PATCH", `/api/posts/${post.id}`, {
          status: gbpPublished ? "published" : "scheduled",
          publishedAt: gbpPublished ? new Date().toISOString() : undefined,
          approvalStatus: "approved",
        });
        if (gbpPublished) gbpCount++;
        savedCount++;
      }
      return { gbpCount, savedCount, total: readyPosts.length };
    },
    onSuccess: ({ gbpCount, savedCount, total }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      const msg = gbpCount === total
        ? `${total} posts published to Google`
        : gbpCount > 0
          ? `${gbpCount} of ${total} published to Google, ${savedCount - gbpCount} scheduled locally`
          : `${savedCount} posts saved & scheduled (Google connection needs re-authorization)`;
      toast({ title: msg });
    },
    onError: (err: Error) => toast({ title: "Bulk publish failed", description: err.message, variant: "destructive" }),
  });

  const handleDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    const postId = e.dataTransfer.getData("text/plain");
    if (!postId) return;
    if (columnId === "scheduled") {
      scheduleMutation.mutate(postId);
    } else if (columnId === "needs-attention") {
      moveToDraftMutation.mutate(postId);
    } else if (columnId === "ready") {
      moveToReadyMutation.mutate(postId);
    }
  };

  const readyCount = columns.find(c => c.id === "ready")?.posts.length || 0;

  if (posts.filter(p => p.status !== "published").length === 0) {
    return (
      <div className="text-center py-16" data-testid="kanban-empty">
        <div className="text-4xl mb-3">📋</div>
        <p className="text-white/50">No drafts or posts in review</p>
        <p className="text-white/20 text-sm mt-1">Generate new content or create a post to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="kanban-board">
      {readyCount > 0 && (
        <div className="flex justify-end">
          <Button
            className="text-white font-semibold"
            style={{ background: DD.accent }}
            onClick={() => bulkPublishMutation.mutate()}
            disabled={bulkPublishMutation.isPending}
            data-testid="button-bulk-publish"
          >
            {bulkPublishMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Publish All ({readyCount})
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map(col => (
          <div
            key={col.id}
            className="rounded-xl min-h-[300px] flex flex-col"
            style={{
              background: dragOverColumn === col.id ? "rgba(255,255,255,0.03)" : DD.surface,
              border: `1px solid ${dragOverColumn === col.id ? col.color + "40" : DD.border}`,
            }}
            onDragOver={e => { e.preventDefault(); setDragOverColumn(col.id); }}
            onDragLeave={() => setDragOverColumn(null)}
            onDrop={e => handleDrop(e, col.id)}
            data-testid={`kanban-column-${col.id}`}
          >
            <div className="px-3 py-2.5 flex items-center gap-2" style={{ borderBottom: `1px solid ${DD.border}` }}>
              <span style={{ color: col.color }}>{col.icon}</span>
              <span className="text-xs font-bold text-white">{col.title}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold" style={{ color: col.color, background: `${col.color}15` }}>
                {col.posts.length}
              </span>
            </div>
            <div className="flex-1 p-2 space-y-2 overflow-y-auto">
              {col.posts.map(post => (
                <KanbanCard
                  key={post.id}
                  post={post}
                  keywords={keywords}
                  onEdit={onClickPost}
                />
              ))}
              {col.posts.length === 0 && (
                <div className="flex items-center justify-center h-full text-xs text-white/20 py-8">
                  No posts
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
